import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsVisitor } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const EXTERNAL_REFERRING_DOMAINS_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.externalReferringDomains.header',
    pageSubheaderKey: 'siteAnalytics.externalReferringDomains.subheader',
    tableKey: 'site-analytics-external-referring-domains',
    dataSource: 'externalReferringDomains',
    exportCategory: undefined,
    searchSupported: false,
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.sources.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.sources.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.externalReferringDomains.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: 'visits',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.externalReferringDomains.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
        orientation: 'vertical',
    },
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'uniqVisitors',
            headerKey: 'siteAnalytics.scaffold.table.column.uniqueVisitors',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'actionsPerVisit',
            headerKey: 'siteAnalytics.scaffold.table.column.actionsPerVisit',
            displayType: 'text',
            width: 'md',
        },
    ],
};

const loadExternalReferringDomainsData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const params = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
    });

    const visitorsParams = statisticsParamsService.buildSummaryParams(
        request.domainId,
        request.dateRange,
        STATISTICS.DATE_FORMAT,
        'referring',
    );

    const [externalReferringDomainsResponse, visitorsResponse] = await Promise.all([
        statsRepoService.getReferrers(params, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getVisitors(visitorsParams).catch(() => null),
    ]);

    const rows = normalizeRows(externalReferringDomainsResponse);
    const aggregateVisitors = extractVisitorSeries(visitorsResponse, 'referring');

    return {
        rows,
        chart: {
            labels: rows.map((row) => getPageLabel(row)),
            primaryValues: rows.map((row) => toInteger(row.visits)),
        },
        summary: null,
        columnAggregates: buildColumnAggregates(aggregateVisitors),
        total: getTotal(externalReferringDomainsResponse, rows.length),
        perPage: getPerPage(externalReferringDomainsResponse, request.perPage),
        page: getCurrentPage(externalReferringDomainsResponse, request.page),
    };
};

function normalizeRows (pages: StatisticsVisitor[]): SiteAnalyticsTableRow[] {
    return pages.map((page, index) => {
        const url = String(page.url || '');
        const label = String(page.label || page.url || '');
        const visits = toInteger(page.visits);
        const bounces = toFloat(page.bounces);
        const actions = toFloat(page.actions);
        const uniqVisitors = toInteger(page.uniq_visitors);

        const bounceRate = visits > 0 ? (bounces / visits) * 100 : 0;
        const actionsPerVisit = visits > 0 ? actions / visits : 0;

        return {
            id: `${url || label || 'row'}-${index}`,
            page: createPageCell(label, url),
            visits,
            uniqVisitors,
            bounceRate: formatPercentage(bounceRate),
            actionsPerVisit: formatDecimal(actionsPerVisit),
        };
    });
}

function buildColumnAggregates (visitors: StatisticsVisitor[]): Record<string, string | null> {
    const totals = visitors.reduce((accumulator, visitor) => {
        return {
            visits: accumulator.visits + toFloat(visitor.visits),
            uniqVisitors: accumulator.uniqVisitors + toFloat(visitor.uniq_visitors),
            bounceRate: accumulator.bounceRate + toFloat(visitor.bounce_rate),
        };
    }, {
        visits: 0,
        uniqVisitors: 0,
        bounceRate: 0,
    });

    const bounceRateTotal = totals.visits > 0 ? (totals.bounceRate / totals.visits) * 100 : 0;

    return {
        visits: formatInteger(totals.visits),
        uniqVisitors: formatInteger(totals.uniqVisitors),
        bounceRate: formatPercentage(bounceRateTotal),
        actionsPerVisit: null,
    };
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const pageCell = toLinkCell(row.page);
    if (pageCell) {
        return pageCell.label;
    }

    return typeof row.page === 'string' ? row.page : String(row.page ?? '');
}

function createPageCell (label: string, url: string): SiteAnalyticsLinkCell | string {
    if (!url) {
        return label;
    }

    return {
        label,
        href: url,
    };
}

function extractVisitorSeries (
    response: unknown,
    sourceType: 'organic' | 'social' | 'referring',
): StatisticsVisitor[] {
    if (Array.isArray(response)) {
        return response as StatisticsVisitor[];
    }

    if (response && typeof response === 'object' && sourceType in response) {
        const series = (response as Record<string, unknown>)[sourceType];
        if (Array.isArray(series)) {
            return series as StatisticsVisitor[];
        }
    }

    return [];
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
    };
}

function toInteger (value: unknown): number {
    return Math.round(toFloat(value));
}

function toFloat (value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
}

function formatInteger (value: number): string {
    return Math.round(value).toLocaleString();
}

function formatDecimal (value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return safeValue.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatPercentage (value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${safeValue.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}

export const EXTERNAL_REFERRING_DOMAINS_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'externalReferringDomains',
    config: EXTERNAL_REFERRING_DOMAINS_CONFIG,
    loadData: loadExternalReferringDomainsData,
};

export const EXTERNAL_REFERRING_DOMAINS_PAGE_CONFIG = EXTERNAL_REFERRING_DOMAINS_DEFINITION.config;
